/**
 * Utility to convert uploaded image files (JPEG, PNG, GIF, BMP, etc.)
 * directly to optimized WebP format client-side using HTML5 Canvas.
 */

export function fileToWebpDataUrl(
  file: File, 
  maxWidthOrQuality: number = 0.85, 
  maxHeight?: number, 
  qualityOption?: number
): Promise<string> {
  let maxWidth = 1200;
  let maxH = 1200;
  let quality = 0.85;

  if (maxWidthOrQuality <= 1 && maxHeight === undefined) {
    quality = maxWidthOrQuality;
  } else {
    maxWidth = maxWidthOrQuality;
    maxH = maxHeight || maxWidth;
    quality = qualityOption !== undefined ? qualityOption : 0.85;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve('');
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (!result) return resolve('');

      const img = new Image();
      img.onload = () => {
        try {
          let origW = img.naturalWidth || img.width || 800;
          let origH = img.naturalHeight || img.height || 800;

          let targetW = origW;
          let targetH = origH;

          if (targetW > maxWidth || targetH > maxH) {
            const ratio = Math.min(maxWidth / targetW, maxH / targetH);
            targetW = Math.round(targetW * ratio);
            targetH = Math.round(targetH * ratio);
          }

          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return resolve(result);
          }
          ctx.drawImage(img, 0, 0, targetW, targetH);
          const webpDataUrl = canvas.toDataURL('image/webp', quality);
          if (webpDataUrl && webpDataUrl.startsWith('data:image/webp')) {
            resolve(webpDataUrl);
          } else {
            resolve(result);
          }
        } catch {
          resolve(result);
        }
      };
      img.onerror = () => resolve(result);
      img.src = result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Normalizes any static or uploaded image string to .webp if it points to a local catalog asset.
 */
export function normalizeImageToWebp(imgUrl: string): string {
  if (!imgUrl) return imgUrl;
  if (imgUrl.startsWith('data:image/') && !imgUrl.startsWith('data:image/webp')) {
    // If it's a data URL of non-webp format, caller should convert via fileToWebpDataUrl
    return imgUrl;
  }
  return imgUrl.replace(/\.(jpg|jpeg|png)($|\?|#)/i, '.webp$2');
}
